/**
 * Memory-safe thumbnail generation for the vault upload queue.
 *
 * Never hand the raw file to an <img> for display: the browser would decode it at
 * native resolution (a 48MP HEIC is ~190 MB of bitmap) and keep that alive for as
 * long as the element exists, which crashed Safari with a queue of iPhone photos.
 * Instead the file is decoded once, downscaled onto a small canvas, exported as a
 * tiny JPEG blob, and the full-resolution decode is released immediately.
 *
 * Both functions resolve to an object URL for the small JPEG (caller must revoke),
 * or null when the browser cannot decode the file — e.g. HEIC on Chrome/Firefox —
 * in which case the caller falls back to a generic icon.
 */

export const THUMBNAIL_MAX_SIZE = 160;

const drawToThumbnailBlob = (
    source: CanvasImageSource,
    sourceWidth: number,
    sourceHeight: number,
    maxSize: number,
): Promise<string | null> =>
    new Promise(resolve => {
        const scale = Math.min(1, maxSize / Math.max(sourceWidth, sourceHeight, 1));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(sourceWidth * scale));
        canvas.height = Math.max(1, Math.round(sourceHeight * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            resolve(null);
            return;
        }

        ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => resolve(blob ? URL.createObjectURL(blob) : null), 'image/jpeg', 0.7);
    });

export const createImageThumbnail = (file: File, maxSize: number = THUMBNAIL_MAX_SIZE): Promise<string | null> =>
    new Promise(resolve => {
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            drawToThumbnailBlob(img, img.naturalWidth, img.naturalHeight, maxSize).then(url => {
                URL.revokeObjectURL(objectUrl);
                resolve(url);
            });
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
        };

        img.src = objectUrl;
    });

export const createVideoThumbnail = (file: File, maxSize: number = THUMBNAIL_MAX_SIZE): Promise<string | null> =>
    new Promise(resolve => {
        const objectUrl = URL.createObjectURL(file);
        const video = document.createElement('video');

        const finish = (url: string | null) => {
            URL.revokeObjectURL(objectUrl);
            resolve(url);
        };

        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;

        video.onloadedmetadata = () => {
            video.currentTime = 0.1;
        };

        video.onseeked = () => {
            drawToThumbnailBlob(video, video.videoWidth, video.videoHeight, maxSize).then(finish);
        };

        video.onerror = () => finish(null);

        video.src = objectUrl;
    });
